import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { siteOrigin } from "./siteUrl";

export type SignupDevice = "web" | "mobile";

const UTM_SOURCE = "3chat_official";
const UTM_MEDIUM = "website";
const UTM_CAMPAIGN = "free_trial";

/** 与 Tailwind `md` 一致：宽屏为 web，否则 mobile（区块内单一按钮时使用） */
export function getSignupDevice(): SignupDevice {
  if (typeof window === "undefined") return "web";
  return window.matchMedia("(min-width: 768px)").matches ? "web" : "mobile";
}

export function buildSignupHref(
  utmContent: string,
  options?: {
    search?: string;
    device?: SignupDevice;
    origin?: string;
  },
): string {
  const origin =
    options?.origin ??
    (typeof window !== "undefined" ? window.location.origin : siteOrigin()) ??
    "";
  const search =
    options?.search ??
    (typeof window !== "undefined" ? window.location.search : "");
  const device = options?.device ?? getSignupDevice();

  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  params.set("utm_source", UTM_SOURCE);
  params.set("utm_medium", UTM_MEDIUM);
  params.set("utm_campaign", UTM_CAMPAIGN);
  params.set("utm_content", utmContent);
  params.set("device", device);

  const base =
    origin || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/signup?${params.toString()}`;
}

export function useTrialSignupHref(
  utmContent: string,
  device?: SignupDevice,
): string {
  const location = useLocation();
  return useMemo(
    () => buildSignupHref(utmContent, { search: location.search, device }),
    [location.search, utmContent, device],
  );
}

/** `/signup` 使用带 UTM 的链接；其它站内/外链保持原逻辑 */
export function resolveSignupLinkHref(
  href: string,
  trackedSignupHref: string,
  origin?: string,
): string {
  if (href === "/signup" || href.startsWith("/signup?")) {
    return trackedSignupHref;
  }
  const base =
    origin ?? (typeof window !== "undefined" ? window.location.origin : siteOrigin());
  if (href.startsWith("/")) {
    return base ? `${base}${href}` : href;
  }
  return href;
}
